import React from 'react';
import { NavLink, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { Home, CreditCard, Send, Receipt, User2, LogOut, FileText, Calendar, Users, PieChart, LifeBuoy } from 'lucide-react';
import { LogoLight } from '../components/Logo';
import { clearSession, getSession } from '../lib/api';
import { ThemeToggle } from '../lib/ThemeContext';
import { useLanguage } from '../lib/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Accueil from './client/Accueil';
import Carte from './client/Carte';
import Virement from './client/Virement';
import Transactions from './client/Transactions';
import Profil from './client/Profil';
import PageRIB from './client/RIB';
import VirementsProgrammes from './client/VirementsProgrammes';
import Beneficiaires from './client/Beneficiaires';
import Releves from './client/Releves';
import FinancePerso from './client/FinancePerso';
import Tickets from './client/Tickets';
import Chatbot from '../components/Chatbot';

export default function DashboardClient() {
  const navigate = useNavigate();
  const { user } = getSession();
  const { t } = useLanguage();
  const logout = () => { clearSession(); navigate('/connexion'); };
  if (!user) return <Navigate to="/connexion" replace />;

  const items = [
    { to: '/dashboard', icon: Home, label: t('nav.home'), end: true },
    { to: '/dashboard/carte', icon: CreditCard, label: t('nav.card') },
    { to: '/dashboard/virement', icon: Send, label: t('nav.transfers') },
    { to: '/dashboard/virements-programmes', icon: Calendar, label: t('nav.scheduledTransfers') },
    { to: '/dashboard/beneficiaires', icon: Users, label: t('nav.beneficiaries') },
    { to: '/dashboard/rib', icon: FileText, label: t('nav.rib') },
    { to: '/dashboard/releves', icon: FileText, label: t('nav.statements') },
    { to: '/dashboard/transactions', icon: Receipt, label: t('nav.transactions') },
    { to: '/dashboard/finance', icon: PieChart, label: t('nav.finance') },
    { to: '/dashboard/support', icon: LifeBuoy, label: t('nav.support') },
    { to: '/dashboard/profil', icon: User2, label: t('nav.profile') },
  ];

  return (
    <div className="dashboard-shell">
      <aside className="sidebar w-64 hidden md:flex flex-col p-5 sticky top-0 h-screen">
        <div className="px-2"><LogoLight /></div>
        <nav className="mt-10 flex-1 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${isActive ? 'active' : 'hover:bg-white/10'}`} data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <it.icon className="w-4 h-4"/> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
          <LanguageSwitcher variant="sidebar" />
          <ThemeToggle variant="sidebar"/>
        </div>
        <button onClick={logout} className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 text-white/80" data-testid="btn-logout">
          <LogOut className="w-4 h-4"/> {t('common.logout')}
        </button>
        <div className="mt-3 text-xs text-white/50">{t('common.connectedAs')}<br/><span className="text-white">{user.prenom} {user.nom}</span></div>
      </aside>
      <main className="flex-1 min-h-screen p-6 md:p-8 max-w-[1400px] w-full mx-auto">
        {/* Mobile nav */}
        <div className="md:hidden mb-4 overflow-x-auto scrollbar-thin -mx-2 px-2 flex gap-2">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({isActive}) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border ${isActive ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700'}`}>
              {it.label}
            </NavLink>
          ))}
          <ThemeToggle variant="pill" />
          <button onClick={logout} className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border bg-white text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700">{t('common.logout')}</button>
        </div>
        <Routes>
          <Route index element={<Accueil />} />
          <Route path="carte" element={<Carte />} />
          <Route path="virement/*" element={<Virement />} />
          <Route path="virements-programmes" element={<VirementsProgrammes />} />
          <Route path="beneficiaires" element={<Beneficiaires />} />
          <Route path="rib" element={<PageRIB />} />
          <Route path="releves" element={<Releves />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="finance" element={<FinancePerso />} />
          <Route path="support" element={<Tickets />} />
          <Route path="profil" element={<Profil />} />
        </Routes>
      </main>
      <Chatbot />
    </div>
  );
}
